import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

const ProblemSection = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="bg-[#0A0A0A] py-20 lg:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-[#C8102E] text-base md:text-lg tracking-[0.2em] font-medium uppercase mb-6">
            {data.label}
          </div>
          
          <h2
            className="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-16 whitespace-pre-line"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.headline}
          </h2>
        </motion.div>

        {/* Problem Cards Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16"
        >
          {data.problems && data.problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.2 }
              }}
              className="group bg-[#111111] border border-[#2A2A2A] rounded-xl p-6 hover:border-[#C8102E]/50 transition-all duration-300"
            >
              {/* Problem Title with Icon */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#C8102E]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertCircle className="w-4 h-4 text-[#C8102E]" />
                </div>
                <h3 
                  className="text-white font-bold text-lg leading-tight"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                >
                  {problem.title}
                </h3>
              </div>
              
              {/* Description */}
              <p 
                className="text-[#F0EBE3]/70 text-sm leading-relaxed pl-11"
                style={{ fontFamily: "'Ubuntu', sans-serif" }}
              >
                {problem.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="w-full h-px bg-[#C8102E] origin-center mb-8"
        ></motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <p
            className="text-[#F0EBE3]/80 text-lg italic"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.bottomLine}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
